import { ArrayInput, NumberInput, SimpleFormIterator, TextInput } from "@/components/admin";
import { memo, useEffect } from "react";
import { useSimpleFormIterator, useSimpleFormIteratorItem } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { HSNFieldWrapper, HSNSuggestionsWrapper } from '../documents/shared/HSNAutocomplete';
import { InventoryItemPickerWrapper } from '../documents/shared/InventoryItemPicker';
import { UQCAutocomplete } from '../documents/shared/UQCAutocomplete';

// ── BoM Item Row Calculator ──────────────────────────────────────────────────
// Mirrors the DocumentItemsTable ItemCalculations but uses estimated_unit_cost
// (BOM terminology) and writes to total_cost.
const BoMItemCalculations = memo(({ source }: { source: string }) => {
    const { setValue } = useFormContext();

    const quantity    = useWatch({ name: `${source}.quantity` }) || 0;
    const unitCost    = useWatch({ name: `${source}.estimated_unit_cost` }) || 0;
    const totalCost   = quantity * unitCost;

    useEffect(() => {
        setValue(`${source}.total_cost`, totalCost, { shouldDirty: true, shouldValidate: false });
    }, [totalCost, source, setValue]);

    return (
        <div className="flex flex-col items-end justify-center w-full px-2 py-1 bg-muted/50 rounded border border-border">
            <span className="font-semibold text-sm text-foreground">
                ₹{totalCost.toFixed(2)}
            </span>
            <span className="text-[10px] text-muted-foreground font-medium">
                Line Total
            </span>
        </div>
    );
});
BoMItemCalculations.displayName = 'BoMItemCalculations';

// ── Unit Field (same wrapper pattern as DocumentItemsTable) ──────────────────
const UnitFieldInside = () => {
    const item     = useSimpleFormIteratorItem();
    const iterator = useSimpleFormIterator();
    if (!item || !iterator) return null;
    const unit = useWatch({ name: `${iterator.source}.${item.index}.unit` });
    return (
        <UQCAutocomplete
            key={unit ?? "__empty__"}
            source="unit"
            label=""
            placeholder="Select UQC"
            className="w-full"
        />
    );
};
const UnitFieldWrapper = () => {
    try { return <UnitFieldInside />; } catch { return null; }
};

// ── Item Calculations Wrapper ────────────────────────────────────────────────
const BoMItemCalcInside = () => {
    const item     = useSimpleFormIteratorItem();
    const iterator = useSimpleFormIterator();
    if (!item || !iterator) return null;
    return <BoMItemCalculations source={`${iterator.source}.${item.index}`} />;
};
const BoMItemCalcWrapper = () => {
    try { return <BoMItemCalcInside />; } catch { return null; }
};

// ── Grand Total Watcher ──────────────────────────────────────────────────────
const BoMGrandTotalWatcher = () => {
    const { setValue } = useFormContext();
    const items = useWatch({ name: 'items' }) || [];
    useEffect(() => {
        const total = items.reduce(
            (acc: number, item: any) => acc + (Number(item?.total_cost) || 0),
            0
        );
        setValue('total_estimated_cost', total, { shouldDirty: true, shouldValidate: false });
    }, [items, setValue]);
    return null;
};

// ── BoMItemsTable ────────────────────────────────────────────────────────────
// Now uses the SAME column structure as DocumentItemsTable (quotation items):
//   description | hsn_code | quantity | unit | estimated_unit_cost | total_cost
// This makes "Create Quotation from BoM" a straight 1-to-1 field copy.
export const BoMItemsTable = ({ source = "items" }: { source?: string }) => (
    <div className="w-full space-y-4">
        <div className="w-full overflow-x-auto border border-border rounded-lg shadow-sm bg-card">
            <div className="min-w-[860px]">
                {/* Header Row — mirrors DocumentItemsTable */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/80 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider items-center hidden md:grid">
                    <div className="col-span-3">Material / Component</div>
                    <div className="col-span-2">HSN / SAC</div>
                    <div className="col-span-1">Qty</div>
                    <div className="col-span-2">UQC</div>
                    <div className="col-span-2">Est. Unit Cost (₹)</div>
                    <div className="col-span-1 text-right">Line Total</div>
                    <div className="col-span-1">Notes</div>
                </div>

                <ArrayInput source={source} label={false}>
                    <SimpleFormIterator
                        className="flex flex-col divide-y divide-slate-100"
                        disableReordering
                        getItemLabel={() => ""}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-3 items-start hover:bg-muted/50 transition-colors">

                            {/* Inventory Picker */}
                            <div className="md:col-span-12 mb-1">
                                <InventoryItemPickerWrapper priceField="estimated_unit_cost" setTaxRate={false} />
                            </div>

                            {/* Description + HSN suggestions */}
                            <div className="md:col-span-3 flex flex-col relative w-full">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mb-1">Material / Component</div>
                                <TextInput
                                    source="description"
                                    label=""
                                    placeholder="Enter material name…"
                                    helperText={false}
                                    className="w-full !mt-0"
                                />
                                <HSNSuggestionsWrapper />
                            </div>

                            {/* HSN Code */}
                            <div className="md:col-span-2 w-full">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mb-1">HSN / SAC</div>
                                <HSNFieldWrapper label="" className="w-full !mt-0" />
                            </div>

                            {/* Quantity */}
                            <div className="md:col-span-1 w-full">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mb-1">Qty</div>
                                <NumberInput
                                    source="quantity"
                                    label=""
                                    placeholder="0"
                                    helperText={false}
                                    className="w-full !mt-0"
                                />
                            </div>

                            {/* Unit (UQC) */}
                            <div className="md:col-span-2 w-full">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mb-1">UQC</div>
                                <UnitFieldWrapper />
                            </div>

                            {/* Estimated Unit Cost */}
                            <div className="md:col-span-2 w-full">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mb-1">Est. Unit Cost (₹)</div>
                                <NumberInput
                                    source="estimated_unit_cost"
                                    label=""
                                    placeholder="0.00"
                                    helperText={false}
                                    className="w-full !mt-0"
                                />
                            </div>

                            {/* Auto-calculated Line Total */}
                            <div className="md:col-span-1 flex items-center justify-end w-full h-10">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mr-auto">Total (₹)</div>
                                <BoMItemCalcWrapper />
                            </div>

                            {/* Notes */}
                            <div className="md:col-span-1 w-full">
                                <div className="md:hidden text-xs font-semibold text-muted-foreground mb-1">Notes</div>
                                <TextInput
                                    source="notes"
                                    label=""
                                    placeholder="Optional note…"
                                    helperText={false}
                                    className="w-full !mt-0"
                                />
                            </div>

                        </div>
                    </SimpleFormIterator>
                </ArrayInput>
            </div>
        </div>
        <BoMGrandTotalWatcher />
    </div>
);
